import React from "react";
import PropTypes from "prop-types";
import { Box, Fade, Stack, SvgIcon, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// One size per kind of place, so the circle and the title always change together.
const SIZES = {
  small: { circle: 44, icon: 18, py: 4, title: { variant: "body2", color: "text.secondary" } },
  medium: { circle: 56, icon: 22, py: 4, title: { variant: "subtitle1", fontWeight: 600 } },
  large: { circle: 72, icon: 28, py: 8, title: { variant: "h6", fontWeight: 600 } },
};

/**
 * The app's one empty-state block. Muted neutrals only: an empty state is not an action, so it
 * stays off the brand accent (a call to action goes in `action`).
 *
 *   - `size="large"` (default) → takes the place of a whole page or panel
 *   - `size="medium"`          → sits in a content pane or a section of a page
 *   - `size="small"`           → a side panel, popover, card or list; with no `icon` this is
 *                                the muted one-liner
 *   - `variant="panel"`        → the filled background and tall layout (metadata panel)
 *
 * Other props land on the root, so call sites keep their own `data-testid`.
 * Wrapped in `<Fade>` by default; pass `fade={false}` to opt out.
 */
const EmptyState = React.memo(({
  title,
  subtitle,
  icon,
  action,
  size = "large",
  variant = "inline",
  fade = true,
  sx,
  ...rest
}) => {
  const { circle, icon: iconSize, py, title: { variant: titleVariant, ...titleSx } } = SIZES[size];

  const content = (
    <Stack
      {...rest}
      spacing={1.5}
      sx={{
        alignItems: "center",
        justifyContent: variant === "panel" ? "flex-start" : "center",
        textAlign: "center",
        ...(variant === "panel" && {
          p: 4,
          height: "100%",
          backgroundColor: "divider",
          borderRadius: 1,
        }),
        ...(variant === "inline" && { py }),
        ...sx,
      }}
    >
      {icon && (
        <Box
          data-testid="empty-state-icon"
          sx={{
            width: circle,
            height: circle,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "action.hover",
            color: "text.secondary",
          }}
        >
          <SvgIcon sx={{ fontSize: iconSize }}>
            <FontAwesomeIcon icon={icon} />
          </SvgIcon>
        </Box>
      )}
      <Typography variant={titleVariant} sx={titleSx}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 360 }}>
          {subtitle}
        </Typography>
      )}
      {action && <Box sx={{ pt: 1 }}>{action}</Box>}
    </Stack>
  );

  return fade ? <Fade in>{content}</Fade> : content;
});

EmptyState.displayName = "EmptyState";

EmptyState.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  icon: PropTypes.object,
  action: PropTypes.node,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  variant: PropTypes.oneOf(["inline", "panel"]),
  fade: PropTypes.bool,
  sx: PropTypes.object,
};

export default EmptyState;
